import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Container, Typography, TextField, Button, MenuItem, Select, FormControl, InputLabel, CircularProgress, Box } from '@mui/material';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { 
  ClassicEditor,
	AccessibilityHelp,
	Autoformat,
	AutoImage,
	Autosave,
	BlockQuote,
	Bold,
	CloudServices,
	Essentials,
	Heading,
	ImageBlock,
	ImageCaption,
	ImageInline,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	SelectAll,
	SimpleUploadAdapter,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
	Undo
 } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import '../App.css';
import { primaryColor } from '../theme';

const targetUsers = [
  { value: 'Staff', label: 'Staff' },
  { value: 'Farmer', label: 'Farmer' },
];

const targetPlatform = [
  { value: 'SMS', label: 'SMS' },
  { value: 'Audio', label: 'Audio' },
  { value: 'Video', label: 'Video' },
  { value: 'Written', label: 'Written' },
  { value: 'Whatsapp', label: 'Whatsapp' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Presentations', label: 'Presentations' }
];

const EditContentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Create refs for input fields
  const topicRef = useRef(null);
  const targetUserRef = useRef(null);
  const valueChainRef = useRef(null);
  const platformRef = useRef(null);
  const contentRef = useRef(null);

  const editorConfig = {
		toolbar: {
			items: [
				'undo',
				'redo',
				'|',
				'heading',
				'|',
				'bold',
				'italic',
				'underline',
				'|',
				'link',
				'insertImage',
				'mediaEmbed',
				'insertTable',
				'blockQuote',
				'|',
				'bulletedList',
				'numberedList',
				'todoList',
				'outdent',
				'indent'
			],
			shouldNotGroupWhenFull: false
		},
    simpleUpload: {
      uploadUrl: `${process.env.REACT_APP_API_BASE_URL}/upload`
    },
		plugins: [
			AccessibilityHelp,
			Autoformat,
			AutoImage,
			Autosave,
			BlockQuote,
			Bold,
			CloudServices,
			Essentials,
			Heading,
			ImageBlock,
			ImageCaption,
			ImageInline,
			ImageInsert,
			ImageInsertViaUrl,
			ImageResize,
			ImageStyle,
			ImageTextAlternative,
			ImageToolbar,
			ImageUpload,
			Indent,
			IndentBlock,
			Italic,
			Link,
			LinkImage,
			List,
			ListProperties,
			MediaEmbed,
			Paragraph,
			PasteFromOffice,
			SelectAll,
			SimpleUploadAdapter,
			Table,
			TableCaption,
			TableCellProperties,
			TableColumnResize,
			TableProperties,
			TableToolbar,
			TextTransformation,
			TodoList,
			Underline,
			Undo
		],
		heading: {
			options: [
				{
					model: 'paragraph',
					title: 'Paragraph',
					class: 'ck-heading_paragraph'
				},
				{
					model: 'heading1',
					view: 'h1',
					title: 'Heading 1',
					class: 'ck-heading_heading1'
				},
				{
					model: 'heading2',
					view: 'h2',
					title: 'Heading 2',
					class: 'ck-heading_heading2'
				},
				{
					model: 'heading3',
					view: 'h3',
					title: 'Heading 3',
					class: 'ck-heading_heading3'
				},
				{
					model: 'heading4',
					view: 'h4',
					title: 'Heading 4',
					class: 'ck-heading_heading4'
				},
				{
					model: 'heading5',
					view: 'h5',
					title: 'Heading 5',
					class: 'ck-heading_heading5'
				},
				{
					model: 'heading6',
					view: 'h6',
					title: 'Heading 6',
					class: 'ck-heading_heading6'
				}
			]
		},
		image: {
			toolbar: [
				'toggleImageCaption',
				'imageTextAlternative',
				'|',
				'imageStyle:inline',
				'imageStyle:wrapText',
				'imageStyle:breakText',
				'|',
				'resizeImage'
			]
		},
		initialData: '',
		link: {
			addTargetToExternalLinks: true,
			defaultProtocol: 'https://',
			decorators: {
				toggleDownloadable: {
					mode: 'manual',
					label: 'Downloadable',
					attributes: {
						download: 'file'
					}
				}
			}
		},
		list: {
			properties: {
				styles: true,
				startIndex: true,
				reversed: true
			}
		},
		placeholder: 'Type or paste your content here!',
		table: {
			contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
		}
	};

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/content/${id}`);
        setContent(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching content:', error);
        setLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setContent({ ...content, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setContent({ ...content, [name]: files[0] });
  };

  const handleContentChange = (event, editor) => {
    const data = editor.getData();
    setContent({ ...content, content: data });
  };

  const validateForm = () => {
    const newErrors = {};
    let firstErrorField = null;

    if (!content.topic) {
      newErrors.topic = 'Topic is required';
      if (!firstErrorField) firstErrorField = topicRef;
    }
    if (!content.targetUser) {
      newErrors.targetUser = 'Target User is required';
      if (!firstErrorField) firstErrorField = targetUserRef;
    }
    if (!content.valueChain) {
      newErrors.valueChain = 'Value Chain is required';
      if (!firstErrorField) firstErrorField = valueChainRef;
    }
    if (!content.platform) {
      newErrors.platform = 'Platform is required';
      if (!firstErrorField) firstErrorField = platformRef;
    }

    setErrors(newErrors);

    if (firstErrorField) {
      firstErrorField.current.focus();
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/content/${id}`, content, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      navigate(`/content/${id}`); // Navigate back to the content detail page after successful update
    } catch (error) {
      console.error('Error updating content:', error);
    }
  };

  const handleCancel = () => {
    navigate(`/content/${id}`); // Navigate back to the content detail page without submitting changes
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!content) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6" color="error">Content not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom style={{ color: primaryColor }}>
        Edit Content
      </Typography>
      <TextField
        label="Topic"
        name="topic"
        value={content.topic}
        onChange={handleInputChange}
        fullWidth
        margin="normal"
        error={!!errors.topic}
        helperText={errors.topic}
        inputRef={topicRef} // Attach ref to input field
      />
      <FormControl fullWidth margin="normal" error={!!errors.targetUser}>
        <InputLabel>Target User</InputLabel>
        <Select
          name="targetUser"
          value={content.targetUser}
          label="Target User"
          onChange={handleInputChange}
          inputRef={targetUserRef} // Attach ref to input field
        >
          {targetUsers.map((user) => (
            <MenuItem key={user.value} value={user.value}>
              {user.label}
            </MenuItem>
          ))}
        </Select>
        {errors.targetUser && <Typography color="error">{errors.targetUser}</Typography>}
      </FormControl>
      <TextField
        label="Value Chain"
        name="valueChain"
        value={content.valueChain}
        onChange={handleInputChange}
        fullWidth
        margin="normal"
        error={!!errors.valueChain}
        helperText={errors.valueChain}
        inputRef={valueChainRef} // Attach ref to input field
      />
      <FormControl fullWidth margin="normal" error={!!errors.platform}>
        <InputLabel>Platform</InputLabel>
        <Select
          name="platform"
          value={content.platform}
          label="Platform"
          onChange={handleInputChange}
          inputRef={platformRef} // Attach ref to input field
        >
          {targetPlatform.map((user) => (
            <MenuItem key={user.value} value={user.value}>
              {user.label}
            </MenuItem>
          ))}
        </Select>
        {errors.platform && <Typography color="error">{errors.platform}</Typography>}
      </FormControl>
      {(content.platform === 'Written' || content.platform === 'Presentations' || content.platform === 'Facebook') && (
        <TextField
          label="Preview Image URL"
          name="image"
          value={content.image}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          error={!!errors.image}
          helperText={errors.image}
        />
      )}
      {content.platform === 'Video' && (
        <Box mt={2}>
          <TextField
            label="Video"
            name="video"
            type="file"
            onChange={handleFileChange}
            fullWidth
            margin="normal"
            error={!!errors.video}
            helperText={errors.video}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}
      {content.platform === 'Audio' && (
        <Box mt={2}>
          <TextField
            label="Audio"
            name="audio"
            type="file"
            onChange={handleFileChange}
            fullWidth
            margin="normal"
            error={!!errors.audio}
            helperText={errors.audio}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}
      {content.platform === 'Presentations' && (
        <Box mt={2}>
          <TextField
            label="Presentation"
            name="presentation"
            type="file"
            onChange={handleFileChange}
            fullWidth
            margin="normal"
            error={!!errors.presentation}
            helperText={errors.presentation}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}
      {(content.platform === 'Presentations' || content.platform === 'Written') && (
        <TextField
          label="Summary"
          name="summary"
          value={content.summary}
          onChange={handleInputChange}
          fullWidth
          margin="normal"
          multiline
          rows={2}
          error={!!errors.summary}
          helperText={errors.summary}
        />
      )}
      {(content.platform !== 'Audio' && content.platform !== 'Video' && content.platform !== 'Presentations') && (
      <Typography variant="body1" gutterBottom>
        Content
      </Typography>
      )}
      {(content.platform !== 'Audio' && content.platform !== 'Video' && content.platform !== 'Presentations') && (
        <CKEditor editor={ClassicEditor} data={content.content} onChange={handleContentChange} config={editorConfig} />
      )}
      {errors.content && <Typography color="error">{errors.content}</Typography>}
      <Box mt={2} mb={2}>
        <Button variant="contained" color="primary" onClick={handleSubmit} sx={{ backgroundColor: primaryColor, mr: 2 }}>
          Save
        </Button>
        <Button variant="contained" color="secondary" onClick={handleCancel}>
          Cancel
        </Button>
      </Box>
    </Container>
  );
};

export default EditContentPage;